import { DatalayerService } from '@analytics/datalayer';
import {
  AnalyticsActionFieldTypes,
  AnalyticsActionTypes,
  ToolTrackValues,
} from '@analytics/models';
import { processAnalyticsValue, ToolTrackingUtils } from '@analytics/utils';
import { Directive, HostListener, inject, Input } from '@angular/core';
import type { RichTextField, TextField } from '@core/jss-proxy';
import { sanitizeFieldValue } from '@core/jss-utils';

/**
 * Configuration interface for field focus tracking.
 */
interface TrackFieldFocusModel extends ToolTrackValues {
  /** The text field to track - can be TextField, RichTextField, or string */
  field: TextField | RichTextField | string;

  /** Whether to include the field value in analytics */
  sendValue: boolean;

  /** Whether to hash the field value before sending */
  hashValue: boolean;

  /** Type of analytics action for the field */
  type: AnalyticsActionFieldTypes;
}

/**
 * Angular directive for tracking form field focus and change events.
 * This directive monitors text input fields and sends analytics events when users
 * interact with the field (focus and completion events).
 *
 * Usage:
 * ```html
 * <input [wlTrackFieldfocus]="{ field: 'username', sendValue: true, step: 1 }">
 * ```
 */
@Directive({
  selector: '[wlTrackFieldfocus]',
  standalone: true,
})
export class TrackFieldfocusDirective {
  readonly #datalayer = inject(DatalayerService);
  readonly #toolTrackingUtils = inject(ToolTrackingUtils);

  // Track state of field interactions
  #hasFired?: 'focus' | 'change';
  #previousValue?: string;

  /**
   * Default configuration values for the directive
   * @private
   */
  readonly #defaultValues: TrackFieldFocusModel = {
    sendValue: false,
    hashValue: false,
    field: '',
    step: 0,
    type: AnalyticsActionTypes.FIELD_FOCUS,
  };

  /**
   * Input binding for tracking configuration
   * @public
   */
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input('wlTrackFieldfocus') trackFieldfocus: TrackFieldFocusModel =
    this.#defaultValues;

  /**
   * Handles the field focus event
   * Fires once per field to track initial focus
   */
  @HostListener('focus')
  async onFocus() {
    // Prevent duplicate focus events
    if (this.#hasFired) {
      return;
    }

    const values = this.#getValues();
    const fieldname = sanitizeFieldValue(values.field);

    // Create and send focus analytics event
    const event = this.#toolTrackingUtils.createToolEvent(
      AnalyticsActionTypes.FIELD_FOCUS,
      {
        // WTA: consider renaming to `fieldName` once Adobe Launch is refactored or removed.
        fieldname,
        result: {
          [fieldname]: processAnalyticsValue(undefined, values),
        },
      },
      await this.#toolTrackingUtils.getToolMetadata(values)
    );

    this.#datalayer.pushEvent(event);

    // Set a focus flag if we haven't fired yet.
    if (!this.#hasFired) {
      this.#hasFired = 'focus';
    }
  }

  /**
   * Handles the field blur (focus out) event
   * Tracks when users complete their interaction with the field
   * @param value - Current field value when focus is lost
   */
  @HostListener('focusout', ['$event.target.value'])
  async onBlur(value: string): Promise<void> {
    const values = this.#getValues();

    // Skip if we've already fired a change event and aren't tracking values
    if (!values.sendValue && this.#hasFired === 'change') {
      return;
    }

    // Skip if tracking values but the value hasn't meaningfully changed
    if (values.sendValue && !this.#hasValueChangedAndIsNotEmpty(value)) {
      return;
    }

    const fieldname = sanitizeFieldValue(values.field);

    // Create and send completion analytics event
    const event = this.#toolTrackingUtils.createToolEvent(
      AnalyticsActionTypes.FIELD_COMPLETE,
      {
        fieldname,
        result: {
          [fieldname]: processAnalyticsValue(
            values.sendValue ? value : undefined,
            values
          ),
        },
      },
      await this.#toolTrackingUtils.getToolMetadata(values)
    );

    this.#datalayer.pushEvent(event);
    this.#hasFired = 'change';
    this.#previousValue = value;
  }

  /**
   * Gets the merged configuration values (defaults + user provided)
   */
  #getValues(): TrackFieldFocusModel {
    return {
      ...this.#defaultValues,
      ...this.trackFieldfocus,
    };
  }

  /**
   * Checks if the field value has changed and is not empty.
   */
  #hasValueChangedAndIsNotEmpty(value: string) {
    return this.#previousValue !== value && value.length > 0;
  }
}
